import React, { useState, useEffect } from 'react';
import { AlertCircle, CheckCircle, XCircle, Search, Loader2, Code, TrendingUp, FileCode } from 'lucide-react';
import * as i18nModule from '../services/i18n';
import apiRequest from '../services/api';
import { authService } from '../services/authService';

const t = i18nModule.t || ((key: string) => key);

interface JsonLdAnalyzerProps {
  onBack?: () => void;
}

interface CategoryScore {
  earned: number;
  possible: number;
  checks: Record<string, {
    passed: boolean;
    points: number;
    name: string;
  }>;
}

interface AnalysisResult {
  score: number;
  maxScore: number;
  categories: Record<string, CategoryScore>;
  details: {
    schemaTypes: string[];
    properties: string[];
    issues: string[];
    recommendations: string[];
  };
  rawData: any[];
}

const JsonLdAnalyzer: React.FC<JsonLdAnalyzerProps> = ({ onBack }) => {
  const [url, setUrl] = useState('');
  const [loading, setLoading] = useState(false);
  const [results, setResults] = useState<AnalysisResult | null>(null);
  const [error, setError] = useState('');
  const [activeTab, setActiveTab] = useState<'summary' | 'checks' | 'recommendations' | 'raw'>('summary');
  const [currentUser, setCurrentUser] = useState<any>(null);

  useEffect(() => {
    // Get current user for credit display
    authService.getCurrentUser().then(user => {
      if (user) setCurrentUser(user);
    }).catch(() => {});
  }, []);

  const analyzeWebsite = async () => {
    if (!url) {
      setError(t('jsonld.urlRequired') || 'Please enter a URL');
      return;
    }

    // Validate URL format
    try {
      new URL(url);
    } catch {
      setError('Please enter a valid URL (e.g., https://example.com)');
      return;
    }

    setLoading(true);
    setError('');
    setResults(null);

    try {
      const response = await apiRequest<{ 
        success: boolean; 
        jsonLd?: any[]; 
        analysis?: any;
        error?: string;
        credits?: number;
      }>('/jsonld/analyze', {
        method: 'POST',
        body: JSON.stringify({ url }),
      });

      if (!response.success) {
        throw new Error(response.error || t('jsonld.analysisFailed') || 'Analysis failed');
      }

      // Update user credits if returned
      if (response.credits !== undefined && currentUser) {
        setCurrentUser({ ...currentUser, credits: response.credits });
      }

      // Use analysis from backend if available, otherwise analyze locally
      let analysis: AnalysisResult;
      if (response.analysis) {
        analysis = {
          score: response.analysis.score || 0,
          maxScore: response.analysis.maxScore || 100,
          categories: response.analysis.categories || {},
          details: response.analysis.details || {
            schemaTypes: [],
            properties: [],
            issues: [],
            recommendations: [],
          },
          rawData: response.jsonLd || [],
        };
      } else {
        analysis = analyzeJsonLd(response.jsonLd || []);
      }

      setResults(analysis);
    } catch (err: any) {
      setError(err.message || t('jsonld.error') || 'Failed to analyze website');
    } finally {
      setLoading(false);
    }
  };

  const analyzeJsonLd = (jsonLdArray: any[]): AnalysisResult => {
    const analysis: AnalysisResult = {
      score: 0,
      maxScore: 100,
      categories: {},
      details: {
        schemaTypes: [],
        properties: [],
        issues: [],
        recommendations: [],
      },
      rawData: jsonLdArray,
    };

    if (!jsonLdArray || jsonLdArray.length === 0) {
      analysis.details.issues.push(t('jsonld.noDataFound') || 'No JSON-LD structured data found');
      analysis.details.recommendations.push(t('jsonld.addJsonLd') || 'Add JSON-LD structured data to improve SEO and AEO');
      return analysis;
    }

    const allData = jsonLdArray.flat();
    const mergedData = Array.isArray(allData) ? allData : [allData];

    mergedData.forEach(item => {
      if (item['@graph']) {
        item['@graph'].forEach((graphItem: any) => {
          if (graphItem['@type']) {
            const types = Array.isArray(graphItem['@type']) ? graphItem['@type'] : [graphItem['@type']];
            analysis.details.schemaTypes.push(...types);
          }
          analysis.details.properties.push(...Object.keys(graphItem));
        });
      } else {
        if (item['@type']) {
          const types = Array.isArray(item['@type']) ? item['@type'] : [item['@type']];
          analysis.details.schemaTypes.push(...types);
        }
        analysis.details.properties.push(...Object.keys(item));
      }
    });

    analysis.details.schemaTypes = [...new Set(analysis.details.schemaTypes)];
    analysis.details.properties = [...new Set(analysis.details.properties)];

    // Calculate score
    let score = 0;
    if (mergedData.some(item => item['@context'])) score += 5;
    if (mergedData.some(item => item['@type'])) score += 5;
    if (mergedData.some(item => item.name)) score += 5;
    if (mergedData.some(item => item.url)) score += 5;
    if (analysis.details.schemaTypes.includes('Organization')) score += 5;
    if (mergedData.some(item => item.logo)) score += 5;
    if (mergedData.some(item => item.telephone || item.email || item.contactPoint)) score += 5;
    if (mergedData.some(item => item.sameAs && Array.isArray(item.sameAs))) score += 5;
    if (mergedData.some(item => item.address)) score += 5;
    const hasSearch = mergedData.some(item =>
      item.potentialAction &&
      (item.potentialAction['@type'] === 'SearchAction' ||
        (Array.isArray(item.potentialAction) && item.potentialAction.some((a: any) => a['@type'] === 'SearchAction')))
    );
    if (hasSearch) score += 10;
    if (analysis.details.schemaTypes.includes('BreadcrumbList')) score += 5;
    if (analysis.details.schemaTypes.includes('WebPage')) score += 5;
    if (mergedData.some(item => item.potentialAction)) score += 5;
    if (mergedData.some(item => item.description)) score += 5;
    if (mergedData.some(item => item['@graph'])) score += 5;
    if (analysis.details.schemaTypes.length > 1) score += 5;
    if (mergedData.some(item => item.review || item.aggregateRating)) score += 5;
    if (mergedData.some(item => item.openingHoursSpecification)) score += 5;
    if (mergedData.some(item => item.geo)) score += 5;

    analysis.score = score;

    if (score < 30) {
      analysis.details.issues.push(t('jsonld.minimalImplementation') || 'Very minimal JSON-LD implementation');
    } else if (score < 60) {
      analysis.details.issues.push(t('jsonld.basicImplementation') || 'Basic JSON-LD present but missing advanced features');
    }

    return analysis;
  };

  const getScoreColor = (score: number) => {
    if (score >= 80) return 'text-emerald-400';
    if (score >= 60) return 'text-yellow-400';
    if (score >= 40) return 'text-orange-400';
    return 'text-red-400';
  };

  const getScoreLabel = (score: number) => {
    if (score >= 80) return t('jsonld.excellent') || 'Excellent';
    if (score >= 60) return t('jsonld.good') || 'Good';
    if (score >= 40) return t('jsonld.fair') || 'Fair';
    return t('jsonld.poor') || 'Poor';
  };

  return (
    <div className="min-h-screen bg-slate-900 pb-12">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="bg-slate-800 rounded-xl border border-slate-700 shadow-lg p-6 mb-6">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-3xl font-bold text-white mb-2 flex items-center gap-3">
                <FileCode className="text-indigo-400" size={32} />
                {(() => {
                  const title = t('jsonld.title');
                  return title && title !== 'jsonld.title' ? title : 'JSON-LD Analyzer & Scorer';
                })()}
              </h1>
              <p className="text-slate-400">
                {(() => {
                  const subtitle = t('jsonld.subtitle');
                  return subtitle && subtitle !== 'jsonld.subtitle' ? subtitle : 'Analyze structured data implementation for SEO and AEO optimization';
                })()}
              </p>
            </div>
            {onBack && (
              <button
                onClick={onBack}
                className="px-4 py-2 bg-slate-700 hover:bg-slate-600 text-white rounded-lg transition-colors"
              >
                {(() => {
                  const back = t('common.back');
                  return back && back !== 'common.back' ? back : 'Back';
                })()}
              </button>
            )}
          </div>
        </div>

        {/* Input Section */}
        <div className="bg-slate-800 rounded-xl border border-slate-700 shadow-lg p-6 mb-6">
          <div className="flex gap-3 mb-3">
            <div className="flex-1">
              <input
                type="url"
                value={url}
                onChange={(e) => setUrl(e.target.value)}
                placeholder={(() => {
                  const placeholder = t('jsonld.urlPlaceholder');
                  return placeholder && placeholder !== 'jsonld.urlPlaceholder' 
                    ? placeholder 
                    : 'Enter website URL (e.g., https://example.com)';
                })()}
                className="w-full px-4 py-3 bg-slate-900 border border-slate-600 text-white rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent placeholder-slate-500"
                onKeyPress={(e) => e.key === 'Enter' && !loading && analyzeWebsite()}
              />
            </div>
            <button
              onClick={analyzeWebsite}
              disabled={loading || !url}
              className="px-6 py-3 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2 transition-colors"
            >
              {loading ? (
                <>
                  <Loader2 className="w-5 h-5 animate-spin" />
                  {(() => {
                    const analyzing = t('jsonld.analyzing');
                    return analyzing && analyzing !== 'jsonld.analyzing' ? analyzing : 'Analyzing...';
                  })()}
                </>
              ) : (
                <>
                  <Search className="w-5 h-5" />
                  {(() => {
                    const analyze = t('jsonld.analyze');
                    return analyze && analyze !== 'jsonld.analyze' ? analyze : 'Analyze';
                  })()}
                </>
              )}
            </button>
          </div>

          {/* Credit Info */}
          {currentUser && (
            <div className="mb-3 text-sm text-slate-400">
              {currentUser.role === 'admin' ? (
                <span className="text-emerald-400">Unlimited (Admin)</span>
              ) : (
                <span>
                  Cost: <span className="font-semibold text-indigo-400">2 credits</span>
                  {currentUser.credits !== undefined && (
                    <span className="ml-2">
                      • You have: <span className="font-semibold">{currentUser.credits} credits</span>
                    </span>
                  )}
                </span>
              )}
            </div>
          )}

          {error && (
            <div className="mt-3 p-3 bg-red-500/10 border border-red-500/30 rounded-lg flex items-start gap-2">
              <AlertCircle className="w-5 h-5 text-red-400 mt-0.5 flex-shrink-0" />
              <span className="text-red-400">{error}</span>
            </div>
          )}
        </div>

        {/* Results Section */}
        {results && (
          <>
            {/* Score Summary */}
            <div className="bg-slate-800 rounded-xl border border-slate-700 shadow-lg p-6 mb-6">
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                <div className="text-center">
                  <div className={`text-5xl font-bold ${getScoreColor(results.score)}`}>
                    {results.score}
                  </div>
                  <div className="text-slate-400 mt-1">
                    {(() => {
                      const outOf = t('jsonld.outOf');
                      return outOf && outOf !== 'jsonld.outOf' ? outOf : 'out of';
                    })()} {results.maxScore}
                  </div>
                  <div className={`text-lg font-semibold mt-2 ${getScoreColor(results.score)}`}>
                    {getScoreLabel(results.score)}
                  </div>
                </div>

                <div className="col-span-2">
                  <h3 className="font-semibold text-white mb-3">
                    {(() => {
                      const breakdown = t('jsonld.categoryBreakdown');
                      return breakdown && breakdown !== 'jsonld.categoryBreakdown' ? breakdown : 'Category Breakdown';
                    })()}
                  </h3>
                  <div className="space-y-2">
                    {Object.entries(results.categories).map(([category, data]) => (
                      <div key={category}>
                        <div className="flex justify-between text-sm mb-1">
                          <span className="capitalize font-medium text-slate-300">
                            {(() => {
                              const catKey = `jsonld.category.${category}`;
                              const catName = t(catKey);
                              return catName && catName !== catKey ? catName : category;
                            })()}
                          </span>
                          <span className="text-slate-400">{data.earned}/{data.possible}</span>
                        </div>
                        <div className="w-full bg-slate-700 rounded-full h-2">
                          <div
                            className={`h-2 rounded-full ${
                              (data.earned / data.possible) >= 0.8
                                ? 'bg-emerald-500'
                                : (data.earned / data.possible) >= 0.5
                                ? 'bg-yellow-500'
                                : 'bg-red-500'
                            }`}
                            style={{ width: `${(data.earned / data.possible) * 100}%` }}
                          ></div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </div>

            {/* Detailed Results Tabs */}
            <div className="bg-slate-800 rounded-xl border border-slate-700 shadow-lg">
              <div className="border-b border-slate-700">
                <nav className="flex -mb-px">
                  {(['summary', 'checks', 'recommendations', 'raw'] as const).map((tab) => (
                    <button
                      key={tab}
                      onClick={() => setActiveTab(tab)}
                      className={`py-3 px-6 border-b-2 font-medium text-sm capitalize transition-colors ${
                        activeTab === tab
                          ? 'border-indigo-500 text-indigo-400'
                          : 'border-transparent text-slate-400 hover:text-slate-300 hover:border-slate-600'
                      }`}
                    >
                      {tab === 'raw' 
                        ? (() => {
                            const raw = t('jsonld.rawJsonLd');
                            return raw && raw !== 'jsonld.rawJsonLd' ? raw : 'Raw JSON-LD';
                          })()
                        : (() => {
                            const tabKey = `jsonld.tab.${tab}`;
                            const tabName = t(tabKey);
                            return tabName && tabName !== tabKey ? tabName : tab;
                          })()}
                    </button>
                  ))}
                </nav>
              </div>
              <div className="p-6">
                {/* Summary Tab */}
                {activeTab === 'summary' && (
                  <div className="space-y-4">
                    <div>
                      <h3 className="font-semibold text-white mb-2">
                        {(() => {
                          const schemaTypes = t('jsonld.schemaTypes');
                          return schemaTypes && schemaTypes !== 'jsonld.schemaTypes' ? schemaTypes : 'Schema Types Found';
                        })()}
                      </h3>
                      <div className="flex flex-wrap gap-2">
                        {results.details.schemaTypes.length > 0 ? (
                          results.details.schemaTypes.map((type, index) => (
                            <span key={index} className="px-3 py-1 bg-indigo-500/20 text-indigo-300 rounded-full text-sm border border-indigo-500/30">
                              {type}
                            </span>
                          ))
                        ) : (
                          <span className="text-slate-500">
                            {(() => {
                              const noTypes = t('jsonld.noSchemaTypes');
                              return noTypes && noTypes !== 'jsonld.noSchemaTypes' ? noTypes : 'No schema types found';
                            })()}
                          </span>
                        )}
                      </div>
                    </div>
                    <div>
                      <h3 className="font-semibold text-white mb-2">
                        {(() => {
                          const properties = t('jsonld.properties');
                          return properties && properties !== 'jsonld.properties' ? properties : 'Properties Detected';
                        })()}
                      </h3>
                      <div className="flex flex-wrap gap-2">
                        {results.details.properties.slice(0, 20).map((prop, index) => (
                          <code key={index} className="px-2 py-1 bg-slate-900 text-slate-300 rounded text-xs border border-slate-700">
                            {prop}
                          </code>
                        ))}
                        {results.details.properties.length > 20 && (
                          <span className="text-slate-500 text-sm">
                            +{results.details.properties.length - 20}{' '}
                            {(() => {
                              const more = t('jsonld.more');
                              return more && more !== 'jsonld.more' ? more : 'more';
                            })()}
                          </span>
                        )}
                      </div>
                    </div>
                    {results.details.issues.length > 0 && (
                      <div>
                        <h3 className="font-semibold text-white mb-2">
                          {(() => {
                            const issues = t('jsonld.issues');
                            return issues && issues !== 'jsonld.issues' ? issues : 'Issues Found';
                          })()}
                        </h3>
                        <ul className="space-y-1">
                          {results.details.issues.map((issue, index) => (
                            <li key={index} className="flex items-start gap-2">
                              <XCircle className="w-5 h-5 text-red-400 mt-0.5 flex-shrink-0" />
                              <span className="text-slate-300">{issue}</span>
                            </li>
                          ))}
                        </ul>
                      </div>
                    )}
                  </div>
                )}

                {/* Checks Tab */}
                {activeTab === 'checks' && (
                  <div className="space-y-6">
                    {Object.entries(results.categories).map(([category, data]) => (
                      <div key={category}>
                        <h3 className="font-semibold text-white mb-3 capitalize">
                          {(() => {
                            const catKey = `jsonld.category.${category}`;
                            const catName = t(catKey);
                            return catName && catName !== catKey ? catName : category;
                          })()}{' '}
                          {(() => {
                            const checks = t('jsonld.checks');
                            return checks && checks !== 'jsonld.checks' ? checks : 'Checks';
                          })()} ({data.earned}/{data.possible}{' '}
                          {(() => {
                            const points = t('jsonld.points');
                            return points && points !== 'jsonld.points' ? points : 'points';
                          })()})
                        </h3>
                        <div className="space-y-2">
                          {Object.entries(data.checks).map(([checkKey, check]) => (
                            <div key={checkKey} className="flex items-center gap-3 p-3 rounded-lg hover:bg-slate-700/50 transition-colors border border-slate-700">
                              {check.passed ? (
                                <CheckCircle className="w-5 h-5 text-emerald-400 flex-shrink-0" />
                              ) : (
                                <XCircle className="w-5 h-5 text-red-400 flex-shrink-0" />
                              )}
                              <span className={check.passed ? 'text-emerald-300' : 'text-slate-400'}>{check.name}</span>
                              <span className="text-slate-500 text-sm ml-auto">{check.points}{' '}
                                {(() => {
                                  const points = t('jsonld.points');
                                  return points && points !== 'jsonld.points' ? points : 'points';
                                })()}
                              </span>
                            </div>
                          ))}
                        </div>
                      </div>
                    ))}
                  </div>
                )}

                {/* Recommendations Tab */}
                {activeTab === 'recommendations' && (
                  <div className="space-y-3">
                    {results.details.recommendations.length > 0 ? (
                      results.details.recommendations.map((rec, index) => (
                        <div key={index} className="flex items-start gap-3 p-3 bg-yellow-500/10 rounded-lg border border-yellow-500/30">
                          <TrendingUp className="w-5 h-5 text-yellow-400 mt-0.5 flex-shrink-0" />
                          <span className="text-yellow-200">{rec}</span>
                        </div>
                      ))
                    ) : (
                      <div className="text-center py-8 text-slate-400">
                        <CheckCircle className="w-12 h-12 mx-auto mb-3 text-emerald-400" />
                        <p>
                          {(() => {
                            const noRec = t('jsonld.noRecommendations');
                            return noRec && noRec !== 'jsonld.noRecommendations' 
                              ? noRec 
                              : 'Great job! No major recommendations at this time.';
                          })()}
                        </p>
                      </div>
                    )}
                  </div>
                )}

                {/* Raw JSON Tab */}
                {activeTab === 'raw' && (
                  <div>
                    {results.rawData.length > 0 ? (
                      <pre className="bg-slate-900 text-slate-100 p-4 rounded-lg overflow-x-auto border border-slate-700">
                        <code>{JSON.stringify(results.rawData, null, 2)}</code>
                      </pre>
                    ) : (
                      <div className="text-center py-8 text-slate-400">
                        <Code className="w-12 h-12 mx-auto mb-3 text-slate-500" />
                        <p>
                          {(() => {
                            const noData = t('jsonld.noDataFound');
                            return noData && noData !== 'jsonld.noDataFound' 
                              ? noData 
                              : 'No JSON-LD data found on this page';
                          })()}
                        </p>
                      </div>
                    )}
                  </div>
                )}
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default JsonLdAnalyzer;
